import { EntityActorDTO } from '#/models/base/EntityActorDTO';
import { KeyDeliveryInventory, KeyDeliveryStatus, KeyDeliveryType, RelatedModel } from '#/models/keyDelivery/KeyDelivery';
import { IdentifierDTO, StateTagProps } from '@zazume/zzm-base';
import { DateVO } from '@zazume/zzm-utils';
import { BasicUnit } from '../unit/BasicUnit';
import { TFunction } from '#/hooks/useI18n';
import { I18NValues } from '#/lib/i18n/generatedKeys';

interface ConstructorParams {
  _id: IdentifierDTO;
  at: DateVO;
  duration: number;
  from: EntityActorDTO;
  organizationId: IdentifierDTO;
  relatedModel: RelatedModel;
  status: KeyDeliveryStatus;
  to: EntityActorDTO;
  unit: BasicUnit;
  note?: string;
  keys?: KeyDeliveryInventory;
  wasDelivered?: boolean;
}

export class KeyDelivery {
  _id: IdentifierDTO;
  at: DateVO;
  duration: number;
  from: EntityActorDTO;
  organizationId: IdentifierDTO;
  relatedModel: RelatedModel;
  status: KeyDeliveryStatus;
  to: EntityActorDTO;
  unit: BasicUnit;
  note?: string;
  keys?: KeyDeliveryInventory;
  wasDelivered?: boolean;

  constructor(params: ConstructorParams){
    this._id = params._id;
    this.at = params.at;
    this.duration = params.duration;
    this.from = params.from;
    this.organizationId = params.organizationId;
    this.relatedModel = params.relatedModel;
    this.status = params.status;
    this.to = params.to;
    this.unit = params.unit;
    this.note = params.note;
    this.keys = params.keys;
    this.wasDelivered = params.wasDelivered;
  }

  static fromRaw(keyDelivery: any): KeyDelivery {
    return new KeyDelivery({
      _id: keyDelivery._id,
      at: DateVO.fromPrimitive(keyDelivery.at),
      duration: keyDelivery.duration,
      from: keyDelivery.from,
      organizationId: keyDelivery.organizationId,
      relatedModel: keyDelivery.relatedModel,
      status: keyDelivery.status,
      to: keyDelivery.to,
      unit: BasicUnit.fromRaw(keyDelivery.unit),
      note: keyDelivery.note,
      keys: keyDelivery.keys,
      wasDelivered: keyDelivery.wasDelivered
    });
  }

  isScheduled(): boolean {
    return this.status === 'scheduled' && this.at.isFuture();
  }

  isCancelled(): boolean {
    return this.status === 'cancelled';
  }

  hasDeliveredFeedback(): boolean {
    return typeof this.wasDelivered === 'boolean';
  }

  isRecent(): boolean {
    const oneWeekAgo = DateVO.now().subtract(7, 'day').withStartOf('date');
    return this.at.isAfter(oneWeekAgo);
  }

  getType(): KeyDeliveryType {
    switch (this.relatedModel.type) {
      case 'Application':
        return 'checkin';
      case 'Contract':
        return 'checkout';
      case 'Unit':
        return 'generic';
    }
  }

  getTagProps(t: TFunction): StateTagProps | undefined {
    return {
      generic: undefined,
      checkin: {
        variant: 'primary' as StateTagProps['variant'],
        text: t('units.detail.keys.history.table.types.checkin')
      },
      checkout: {
        variant: 'red' as StateTagProps['variant'],
        text: t('units.detail.keys.history.table.types.checkout')
      }
    }[this.getType()];
  }

  getKeysInventory(t: TFunction): string[] | undefined {
    if (!this.keys) {
      return undefined;
    }
    return Object.entries(this.keys)
      .filter(([, value]) => (value as number) > 0)
      .map(([type, value]) => `${t(`units.detail.keys.fields.${type}` as I18NValues)} (${value})`);
  }

}
